import { config } from '../config/Config';
import { auth } from './firebase';

export class APIError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = 'APIError';
  }
}

class HttpClient {
  static countryCode: string | null = null;

  static async fetchCountryCode() {
    if (!this.countryCode) {
      try {
        const response = await fetch('https://dashtoon.ai/cdn-cgi/trace');
        const text = await response.text();
        const match = text.match(/loc=(\w+)/);
        if (match && match[1]) {
          this.countryCode = match[1].toUpperCase();
        }
      } catch (error) {
        console.error('Error fetching country code:', error);
      }
    }
  }

  static async uploadFile(
    path: string,
    file: File,
    onProgress: (progress: number) => void
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      await this.fetchCountryCode();

      await auth.authStateReady();
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(JSON.parse(xhr.responseText));
        }
      };

      xhr.onerror = () => {
        reject('Network error');
      };

      xhr.open('POST', `${config.baseUrl}${path}`);

      if (idToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${idToken}`);
        xhr.setRequestHeader('x-tenant', 'studio');
        if (this.countryCode) {
          xhr.setRequestHeader('x-country', this.countryCode);
        }
      }

      const formData = new FormData();
      formData.append('file', file);

      xhr.send(formData);
    });
  }

  static async post(
    path: string,
    body: any,
    additionalHeaders: {} = {},
    showErrorToast: boolean = true,
    useApiError: boolean = false,
    baseUrl: string = config.baseUrl ?? ''
  ): Promise<any> {
    await this.fetchCountryCode();
    await auth.authStateReady();
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    console.log("UID:", {uid: auth.currentUser?.uid})
    console.log('CMS Token Info:', {
      path,
      userEmail: auth.currentUser?.email,
      tokenExists: !!idToken,
      tokenLength: idToken,
      tokenPrefix: idToken?.substring(0, 20) + '...'
    });
    console.log(baseUrl)
    const finalPath = `${baseUrl}${path}`;
    const response = await fetch(finalPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'x-tenant': 'studio',
        ...(this.countryCode ? { 'x-country': this.countryCode } : {}),
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...additionalHeaders,
      },
      body: JSON.stringify(body),
    });
    
    if (response.ok) {
      return await response.json();
    } else {
      const message = (await response.json()) as { message: string; code: number };
      console.error('API Error:', message.message, 'Status:', response.status);
      if (useApiError) throw new APIError(message.message, response.status);
      else throw Error(message.message);
    }
  }

  static async postWithoutBody(
    path: string,
    additionalHeaders: {} = {},
    showErrorToast: boolean = true
  ): Promise<any> {
    await this.fetchCountryCode();
    await auth.authStateReady();
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    const finalPath = `${config.baseUrl}${path}`;
    const response = await fetch(finalPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'x-tenant': 'studio',
        ...(this.countryCode ? { 'x-country': this.countryCode } : {}),
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...additionalHeaders,
      },
    });
    
    if (response.ok) {
      return await response.json();
    } else {
      const message = await response.json();
      console.error('API Error:', message.message, 'Status:', response.status);
      throw Error(message.message);
    }
  }

  static async get(
    path: string,
    additionalHeaders: {} = {},
    showErrorToast: boolean = true
  ): Promise<any> {
    await this.fetchCountryCode();
    await auth.authStateReady();
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    console.log('[AUTH] Access token (GET %s):\n%s', path, idToken);
    const finalPath = `${config.baseUrl}${path}`;
    const response = await fetch(finalPath, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'x-tenant': 'studio',
        ...(this.countryCode ? { 'x-country': this.countryCode } : {}),
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...additionalHeaders,
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      const message = await response.json();
      console.error('API Error:', message.message, 'Status:', response.status);
      throw new Error(message.message);
    }
    if (response.status === 200) {
      return await response.json();
    }
    return response;
  }

  static async putWithoutBaseUrl(
    path: string,
    body: any,
    additionalHeaders: {},
    onProgress: (percentage: number) => void,
    showErrorToast: boolean = true
  ): Promise<any> {
    await this.fetchCountryCode();

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentage = (event.loaded / event.total) * 100;
          onProgress(Math.round(percentage));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.response);
        } else {
          const errorResponse = xhr.response;
          console.error('API Error:', errorResponse.message, 'Status:', xhr.status);
          reject(new Error(errorResponse.message));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Network error'));
      };

      xhr.open('PUT', path);
      xhr.setRequestHeader('x-tenant', 'studio');

      Object.entries(additionalHeaders).forEach(([key, value]) => {
        if (typeof value === 'string') {
          xhr.setRequestHeader(key, value);
        }
      });

      xhr.send(body);
    });
  }

  static async put(
    path: string,
    body: any,
    additionalHeaders: {} = {},
    showErrorToast: boolean = true
  ): Promise<any> {
    await this.fetchCountryCode();
    await auth.authStateReady();
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    const finalPath = `${config.baseUrl}${path}`;
    const response = await fetch(finalPath, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'x-tenant': 'studio',
        ...(this.countryCode ? { 'x-country': this.countryCode } : {}),
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...additionalHeaders,
      },
      body: JSON.stringify(body),
    });
    
    if (response.ok) {
      return await response.json();
    } else {
      const message = await response.json();
      console.error('API Error:', message.message, 'Status:', response.status);
      throw Error(message.message);
    }
  }

  static async delete(
    path: string,
    body: any,
    additionalHeaders: {} = {},
    showErrorToast: boolean = true
  ): Promise<any> {
    await this.fetchCountryCode();
    await auth.authStateReady();
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    const finalPath = `${config.baseUrl}${path}`;
    const response = await fetch(finalPath, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'x-tenant': 'studio',
        ...(this.countryCode ? { 'x-country': this.countryCode } : {}),
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...additionalHeaders,
      },
      body: JSON.stringify(body),
    });
    
    if (response.status === 200) {
      return await response.json();
    } else {
      const message = await response.json();
      console.error('API Error:', message.message, 'Status:', response.status);
      throw Error(message.message);
    }
  }

  static async patch(
    path: string,
    body: any,
    additionalHeaders: {} = {},
    showErrorToast: boolean = true
  ): Promise<any> {
    await this.fetchCountryCode();
    await auth.authStateReady();
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    const finalPath = `${config.baseUrl}${path}`;
    const response = await fetch(finalPath, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'x-tenant': 'studio',
        ...(this.countryCode ? { 'x-country': this.countryCode } : {}),
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...additionalHeaders,
      },
      body: JSON.stringify(body),
    });
    
    if (response.status === 200) {
      return await response.json();
    } else {
      const message = await response.json();
      console.error('API Error:', message.message, 'Status:', response.status);
      throw Error(message.message);
    }
  }
}

export default HttpClient;